import { useLayoutEffect } from 'react';

// ProseMirror's default scroll-to-selection also scrolls ancestor containers
// and the document. In a keyboard-sized editor only the text pane may scroll.
export function scrollEditorSelectionIntoView(view) {
  const pane = view.dom.closest('.editor-wrapper');
  if (!pane || !view.hasFocus() || pane.clientHeight === 0) return true;
  const caret = view.coordsAtPos(view.state.selection.head);
  const bounds = pane.getBoundingClientRect();
  const inset = Math.min(8, pane.clientHeight / 4);
  const top = bounds.top + inset;
  const bottom = bounds.bottom - inset;
  if (caret.bottom > bottom) pane.scrollTop += caret.bottom - bottom;
  else if (caret.top < top) pane.scrollTop -= top - caret.top;
  return true;
}

export default function useEditorViewport(editor, viewport) {
  useLayoutEffect(() => {
    // Scope the iOS document scroll lock to this editor. Text, selection,
    // pinch zoom and the independently scrollable event remain native.
    const root = document.documentElement;
    root.classList.add('comment-editor-open');
    return () => root.classList.remove('comment-editor-open');
  }, []);

  useLayoutEffect(() => {
    if (!editor || editor.isDestroyed || !editor.view?.dom) return undefined;
    let frame;
    const revealCaret = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!editor.isDestroyed) scrollEditorSelectionIntoView(editor.view);
      });
    };
    const pane = editor.view.dom.closest('.editor-wrapper');
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(revealCaret) : null;
    if (pane) observer?.observe(pane);
    const documentChanged = ({ transaction }) => {
      if (transaction.docChanged) revealCaret();
    };
    // Restoring remote text or decoding an inserted image can move the caret
    // without a browser resize or an explicit ProseMirror scroll request.
    editor.on('transaction', documentChanged);
    pane?.addEventListener('load', revealCaret, true);
    // Resize/pan after opening the keyboard changes the visible text area.
    // Deliberate scrolling inside that area must not snap back to the cursor.
    revealCaret();
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      editor.off('transaction', documentChanged);
      pane?.removeEventListener('load', revealCaret, true);
    };
  }, [editor, viewport.height, viewport.offsetTop, viewport.keyboardOpen]);
}
